import React, { useEffect, useState } from 'react'
import { MapContainer, TileLayer, useMapEvents } from 'react-leaflet'

const DEFAULT_CENTER = [51.5074, -0.1278] // London
const DEFAULT_ZOOM = 17

function MapEventHandler({ onCenterChange }) {
  const map = useMapEvents({
    moveend: () => {
      const center = map.getCenter()
      onCenterChange(center.lat, center.lng)
    },
    zoomend: () => {
      const center = map.getCenter()
      onCenterChange(center.lat, center.lng)
    }
  })
  return null
}

export default function MapLocationPicker({
  lat,
  lng,
  onChange,
  label,
  hint,
  error,
  height = 300,
  zoom = DEFAULT_ZOOM,
  defaultCenter = DEFAULT_CENTER,
  showCoordinates = true
}) {
  const [mapLat, setMapLat] = useState(() => {
    const parsed = parseFloat(lat)
    return !isNaN(parsed) ? parsed : defaultCenter[0]
  })
  const [mapLng, setMapLng] = useState(() => {
    const parsed = parseFloat(lng)
    return !isNaN(parsed) ? parsed : defaultCenter[1]
  })
  const [isInitialized, setIsInitialized] = useState(false)

  // Update internal state when external values change (only on initial load)
  useEffect(() => {
    if (!isInitialized) {
      const parsedLat = parseFloat(lat)
      const parsedLng = parseFloat(lng)
      if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
        setMapLat(parsedLat)
        setMapLng(parsedLng)
      }
      setIsInitialized(true)
    }
  }, [lat, lng, isInitialized])

  const handleCenterChange = (newLat, newLng) => {
    const roundedLat = Math.round(newLat * 1000000) / 1000000
    const roundedLng = Math.round(newLng * 1000000) / 1000000
    setMapLat(roundedLat)
    setMapLng(roundedLng)
    onChange({ lat: roundedLat, lng: roundedLng })
  }

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      <div className="relative rounded-lg overflow-hidden border border-gray-300">
        <MapContainer
          center={[mapLat, mapLng]}
          zoom={zoom}
          style={{ height: `${height}px`, width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapEventHandler onCenterChange={handleCenterChange} />
        </MapContainer>

        {/* Fixed center pin overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[1000]">
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            className="text-sky-600 drop-shadow-lg"
            style={{ transform: 'translateY(-20px)' }}
          >
            <path
              d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"
              fill="currentColor"
            />
          </svg>
        </div>
      </div>

      {hint && !error && (
        <p className="text-sm text-gray-500">{hint}</p>
      )}

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {showCoordinates && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Latitude</label>
            <input
              type="text"
              readOnly
              value={mapLat || ''}
              className="block w-full rounded-md border-gray-300 bg-gray-50 text-gray-700 text-sm shadow-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Longitude</label>
            <input
              type="text"
              readOnly
              value={mapLng || ''}
              className="block w-full rounded-md border-gray-300 bg-gray-50 text-gray-700 text-sm shadow-sm"
            />
          </div>
        </div>
      )}
    </div>
  )
}
